/**
 * useWorkshopTypeWrites — the two list-level writes on the workshop-type
 * catalogue (BON-391): flip `isActive`, and hard-delete.
 *
 * Both invalidate the SHARED catalogue query key on success, so the dashboards'
 * type pickers pick the change up immediately instead of serving a stale cache
 * for the rest of the `staleTime` window.
 *
 * Errors are surfaced as a single message string rather than thrown: the one
 * the caller most needs to show is the guarded-delete 409 ("referenced by N
 * workshop(s) — deactivate instead"), which is a normal outcome of a legitimate
 * click, not an exception.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  deleteWorkshopType,
  updateWorkshopType,
  WORKSHOP_TYPES_QUERY_KEY,
  type WorkshopType,
} from '../../../api/workshop-types';

import { workshopTypeErrorMessage } from './WorkshopTypeFormState';

export interface WorkshopTypeWrites {
  /** Toggle a type between active and inactive. */
  toggleActive: (type: WorkshopType) => void;
  /** Hard-delete a type. A type in use comes back as an `error` message. */
  remove: (type: WorkshopType) => void;
  /** True while either write is in flight — callers disable their affordances. */
  writing: boolean;
  /** Last write failure, or '' when the last write succeeded. */
  error: string;
}

export function useWorkshopTypeWrites(): WorkshopTypeWrites {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const handlers = {
    onSuccess: () => {
      setError('');
      return queryClient.invalidateQueries({ queryKey: WORKSHOP_TYPES_QUERY_KEY });
    },
    onError: (err: unknown) =>
      setError(workshopTypeErrorMessage(err, 'That change could not be saved.')),
  };

  const activeMutation = useMutation({
    mutationFn: (type: WorkshopType) => updateWorkshopType(type.id, { isActive: !type.isActive }),
    ...handlers,
  });
  const deleteMutation = useMutation({
    mutationFn: (type: WorkshopType) => deleteWorkshopType(type.id),
    ...handlers,
  });

  return {
    toggleActive: activeMutation.mutate,
    remove: deleteMutation.mutate,
    writing: activeMutation.isPending || deleteMutation.isPending,
    error,
  };
}
